// Module 16(b) — the column/template/breakdown engine behind the wizard's Data step.
//
// Everything here is the REBUILD half of `DataExportStep2.vue` (929 ln) that is not the date range
// (that half lives in `useExportDateRange`). The pure functions below are exported on their own so
// the rules the legacy component expressed as inline template expressions become testable:
//   • the TikTok mandatory-column set, which legacy inlined TWICE (`:842-847` guard, `:908-928`
//     auto-add). It is declared exactly once here.
//   • dimension prepends / metric appends (`:860-864`)
//   • breakdown narrowing against `breakdown_combinations` (`:792-808`)
//   • the `<d|w|m><n>` cohort-period codec (`:414-420` encode, `:441-452` decode)
//   • the default template = first `custom_` twin, columns from the NON-prefixed twin (`:509-518`)
//
// NOT ported: `activePlatform` (`:357`, `:871-880`) — declared, maintained by a deep watcher, and
// never read by anything. It is dead in v3 and absent here.
import { computed, watch, type ComputedRef } from 'vue'

import type {
  ExportBreakdown,
  ExportColumnSelection,
  ExportPlatform,
  ExportTemplate,
} from '../models/exportJob.types'
import type { DataExportStore } from '../stores/dataExportStore'

/**
 * The six TikTok columns the export service requires. Declared ONCE (the AC): every guard, every
 * auto-add and every disabled checkbox reads this constant.
 */
export const TIKTOK_MANDATORY_COLUMNS: readonly string[] = Object.freeze([
  'campaign_id',
  'advertiser_id',
  'ad_group_id',
  'ad_id',
  'device_brand_id',
  'province_id',
])

const TIKTOK_PLATFORM: ExportPlatform = 'tiktok_ads'
const CUSTOM_TEMPLATE_PREFIX = 'custom_'

export const COHORT_PERIOD_TYPES = ['d', 'w', 'm'] as const
export type CohortPeriodType = (typeof COHORT_PERIOD_TYPES)[number]

export const COHORT_PERIOD_LABEL_KEYS: Readonly<Record<CohortPeriodType, string>> = Object.freeze({
  d: 'dataExport.data.cohort.day',
  w: 'dataExport.data.cohort.week',
  m: 'dataExport.data.cohort.month',
})

export interface MetricConfig {
  eventSlug: string[]
  cohortType: CohortPeriodType
  cohortValue: number | null
}

/** `:842-847` — mandatory only while TikTok is one of the chosen platforms. */
export function isMandatoryExportColumn(
  field: string | undefined,
  platforms: readonly ExportPlatform[],
): boolean {
  return (
    field !== undefined &&
    platforms.includes(TIKTOK_PLATFORM) &&
    TIKTOK_MANDATORY_COLUMNS.includes(field)
  )
}

/** `:810-812` — only the `custom_` twins are offered in the template select. */
export function customExportTemplates(
  templates: readonly ExportTemplate[],
): readonly ExportTemplate[] {
  return templates.filter((template) => template.identifier.startsWith(CUSTOM_TEMPLATE_PREFIX))
}

/**
 * `:515-517` — picking `custom_x` seeds the selection from `x`'s `dimensions ∪ metrics`. The
 * `identifier`→`field` / `name`→`header` / metrics-`numeric` aliasing already happened in
 * `exportJobService.toExportTemplate`, so this is a pure union, not a second mapper.
 */
export function columnsForTemplate(
  templates: readonly ExportTemplate[],
  identifier: string,
): ExportColumnSelection[] {
  const twinId = identifier.replace(CUSTOM_TEMPLATE_PREFIX, '')
  const twin = templates.find((template) => template.identifier === twinId)
  return [...(twin?.dimensions ?? []), ...(twin?.metrics ?? [])]
}

/** `:849-869` — a metric APPENDS, a dimension PREPENDS, and a second click deselects. */
export function toggleExportColumn(
  selected: readonly ExportColumnSelection[],
  column: ExportColumnSelection,
): ExportColumnSelection[] {
  if (selected.some((item) => item.field === column.field)) {
    return selected.filter((item) => item.field !== column.field)
  }
  return column.numeric ? [...selected, { ...column }] : [{ ...column }, ...selected]
}

/**
 * `:908-928` — TikTok's mandatory dimensions are added to the selection automatically. Returns the
 * SAME reference when there is nothing to add, so the caller's watcher cannot loop. Legacy read
 * `viewedTemplate.value.dimensions` unguarded and threw whenever the template had not loaded yet.
 */
export function withMandatoryColumns(
  selected: readonly ExportColumnSelection[],
  offered: readonly ExportColumnSelection[],
  platforms: readonly ExportPlatform[],
): readonly ExportColumnSelection[] {
  if (!platforms.includes(TIKTOK_PLATFORM)) return selected
  const missing = offered.filter(
    (column) =>
      TIKTOK_MANDATORY_COLUMNS.includes(column.field) &&
      !selected.some((item) => item.field === column.field),
  )
  return missing.length === 0 ? selected : [...selected, ...missing]
}

/** `identifier.split('_').slice(0, -1).join('_')` — `age_breakdown` is combination name `age`. */
function breakdownBase(identifier: string): string {
  return identifier.split('_').slice(0, -1).join('_')
}

/**
 * `:792-808` — with nothing picked every breakdown is offered; after that the offer narrows to the
 * names reachable from a `breakdown_combinations` entry that already contains every pick. Legacy
 * spread `find(...)` unguarded and emitted `{}` for a name with no matching breakdown, which put
 * blank rows in the dropdown; unmatched names are dropped here.
 */
export function narrowBreakdownOptions(
  selected: readonly ExportBreakdown[],
  offered: readonly ExportBreakdown[],
  combinations: readonly (readonly string[])[],
): readonly ExportBreakdown[] {
  if (selected.length === 0) return offered
  const valid = combinations.filter((combination) =>
    selected.every((pick) => combination.includes(breakdownBase(pick.identifier))),
  )
  const names = [...new Set(valid.flat())]
  return names
    .map((name) => offered.find((option) => breakdownBase(option.identifier) === name))
    .filter((option): option is ExportBreakdown => option !== undefined)
}

/** `:416-419` — `d` + `7` → `d7`. An absent value means "no cohort period configured". */
export function encodeCohortPeriod(
  type: CohortPeriodType,
  value: number | null | undefined,
): string | null {
  return value === null || value === undefined || !Number.isFinite(value)
    ? null
    : `${type}${String(value)}`
}

/** `:441-452` — `d7` → `{ type: 'd', value: 7 }`; an unknown or absent code falls back to `d`. */
export function decodeCohortPeriod(encoded: string | null | undefined): {
  cohortType: CohortPeriodType
  cohortValue: number | null
} {
  const head = encoded?.charAt(0)
  const cohortType = COHORT_PERIOD_TYPES.find((type) => type === head) ?? COHORT_PERIOD_TYPES[0]
  const parsed = Number(encoded?.slice(1))
  return {
    cohortType,
    cohortValue: encoded && Number.isFinite(parsed) && encoded.length > 1 ? parsed : null,
  }
}

function eventSlugList(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return [...value]
  return value === undefined || value === '' ? [] : [value]
}

/** `:438-453` — read a metric's persisted config back out of the column it was written onto. */
export function readMetricConfig(column: ExportColumnSelection): MetricConfig {
  return {
    eventSlug: eventSlugList(column.selected_event_slug),
    ...decodeCohortPeriod(column.selected_cohort_period),
  }
}

/**
 * `:421-432` — write the config back onto the matching column. Empty selections REMOVE the key
 * rather than writing `[]`/`null`: `buildPayload` treats any present value as truthy, so an empty
 * array would ship `platform_details.event_slug = { metric: [] }`.
 */
export function applyMetricConfig(
  columns: readonly ExportColumnSelection[],
  field: string,
  config: MetricConfig,
): ExportColumnSelection[] {
  const cohortPeriod = encodeCohortPeriod(config.cohortType, config.cohortValue)
  return columns.map((column) => {
    if (column.field !== field) return column
    const { selected_event_slug: _slug, selected_cohort_period: _period, ...rest } = column
    return {
      ...rest,
      ...(config.eventSlug.length > 0 ? { selected_event_slug: [...config.eventSlug] } : {}),
      ...(cohortPeriod === null ? {} : { selected_cohort_period: cohortPeriod }),
    }
  })
}

/** `:814-826` — selected columns float to the top of the dual list, in selection order. */
function orderBySelection(
  columns: readonly ExportColumnSelection[],
  selected: readonly ExportColumnSelection[],
): ExportColumnSelection[] {
  const order = selected.map((column) => column.field)
  return [...columns].sort((left, right) => {
    const leftIndex = order.indexOf(left.field)
    const rightIndex = order.indexOf(right.field)
    if (leftIndex === -1 && rightIndex === -1) return 0
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    return leftIndex - rightIndex
  })
}

export interface UseExportColumns {
  customTemplates: ComputedRef<readonly ExportTemplate[]>
  templateId: ComputedRef<string | null>
  dimensions: ComputedRef<ExportColumnSelection[]>
  metrics: ComputedRef<ExportColumnSelection[]>
  selectedColumns: ComputedRef<readonly ExportColumnSelection[]>
  breakdownOptions: ComputedRef<readonly ExportBreakdown[]>
  isSelected: (field: string) => boolean
  isMandatory: (field: string) => boolean
  selectTemplate: (identifier: string) => void
  toggleColumn: (column: ExportColumnSelection) => void
  setColumns: (columns: readonly ExportColumnSelection[]) => void
}

/**
 * Binds the rules above to the draft. Every write goes through `store.updateDraft`, so the step
 * components stay render-only and the draft remains the single source of truth.
 */
export function useExportColumns(store: DataExportStore): UseExportColumns {
  const draft = computed(() => store.draft)
  const platforms = computed<readonly ExportPlatform[]>(() => draft.value?.platforms ?? [])
  const selectedColumns = computed<readonly ExportColumnSelection[]>(
    () => draft.value?.columns ?? [],
  )
  const customTemplates = computed(() => customExportTemplates(store.templates))
  const templateId = computed(() => draft.value?.template?.identifier ?? null)

  // `:788-790` — the OFFER comes from the selected `custom_` template itself, while the initial
  // SELECTION comes from its non-prefixed twin (`columnsForTemplate`). That asymmetry is legacy's.
  const activeTemplate = computed(
    () => store.templates.find((template) => template.identifier === templateId.value) ?? null,
  )
  const offeredColumns = computed(() => [
    ...(activeTemplate.value?.dimensions ?? []),
    ...(activeTemplate.value?.metrics ?? []),
  ])
  const orderedColumns = computed(() =>
    orderBySelection(offeredColumns.value, selectedColumns.value),
  )
  const dimensions = computed(() => orderedColumns.value.filter((column) => !column.numeric))
  const metrics = computed(() => orderedColumns.value.filter((column) => column.numeric))
  const breakdownOptions = computed(() =>
    narrowBreakdownOptions(
      draft.value?.breakdowns ?? [],
      activeTemplate.value?.breakdowns ?? [],
      store.breakdownCombinations,
    ),
  )

  function setColumns(columns: readonly ExportColumnSelection[]): void {
    store.updateDraft({ columns: [...columns] })
  }

  function selectTemplate(identifier: string): void {
    store.updateDraft({
      template: { identifier },
      columns: columnsForTemplate(store.templates, identifier),
    })
  }

  function toggleColumn(column: ExportColumnSelection): void {
    if (isMandatory(column.field)) return
    setColumns(toggleExportColumn(selectedColumns.value, column))
  }

  function isSelected(field: string): boolean {
    return selectedColumns.value.some((column) => column.field === field)
  }

  function isMandatory(field: string): boolean {
    return isMandatoryExportColumn(field, platforms.value)
  }

  // `:897-906` — the first `custom_` template is the default the moment the catalog arrives.
  watch(
    customTemplates,
    (list) => {
      const first = list[0]
      if (!draft.value || templateId.value !== null || first === undefined) return
      selectTemplate(first.identifier)
    },
    { immediate: true },
  )

  // `:908-928`, but reactive instead of `onMounted`: legacy ran the auto-add once, so a platform
  // or template chosen AFTER mount never gained its mandatory columns.
  watch(
    [offeredColumns, platforms],
    () => {
      if (!draft.value) return
      const next = withMandatoryColumns(
        selectedColumns.value,
        offeredColumns.value,
        platforms.value,
      )
      if (next !== selectedColumns.value) setColumns(next)
    },
    { immediate: true },
  )

  return {
    customTemplates,
    templateId,
    dimensions,
    metrics,
    selectedColumns,
    breakdownOptions,
    isSelected,
    isMandatory,
    selectTemplate,
    toggleColumn,
    setColumns,
  }
}
